import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setApiDepot, setDepotBrands } from "../lib/api";

const AuthContext = createContext(null);

// Persist the picked depot across reloads so operators don't have to
// re-pick after every refresh. Scoped by user email so switching accounts
// on the same browser doesn't leak the previous user's selection.
const DEPOT_STORAGE_KEY = (email) => `portside.depot:${(email || "").toLowerCase()}`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  const [depots, setDepots] = useState({ depots: [], my_depots: [], default_depot: "GH-TEM", can_pick_all: false });
  const [depot, setDepotState] = useState(""); // "" = ALL, else a depot code

  // Load depots after login — needed to populate the top-nav picker. Also
  // picks a sensible default: ALL for admins, the user's only depot for
  // single-depot users, saved selection otherwise.
  const loadDepots = useCallback(async (u) => {
    try {
      const { data } = await api.get("/depots");
      setDepots(data);
      // Cache brand info keyed by depot code so PDF exporters can look up
      // the right letterhead per invoice.depot_code.
      const brandMap = {};
      for (const d of data.depots || []) {
        if (d.brand) brandMap[d.code] = { ...d.brand, country: d.country, depot_code: d.code };
      }
      setDepotBrands(brandMap);
      const saved = localStorage.getItem(DEPOT_STORAGE_KEY(u?.email));
      const validSaved = saved && (saved === "" || saved === "ALL" || data.my_depots?.includes(saved));
      let pick = "";
      if (validSaved) pick = (saved === "ALL") ? "" : saved;
      else if (u?.role === "admin") pick = "";
      else if ((data.my_depots || []).length === 1) pick = data.my_depots[0];
      else pick = "";  // ALL by default for multi-depot non-admins
      setDepotState(pick);
      setApiDepot(pick);
    } catch {
      setDepots({ depots: [], my_depots: [], default_depot: "GH-TEM", can_pick_all: false });
      setDepotState("");
      setApiDepot("");
    }
  }, []);

  const setDepot = useCallback((code) => {
    const picked = (code || "").toUpperCase().trim();
    setDepotState(picked);
    setApiDepot(picked);
    if (user?.email) localStorage.setItem(DEPOT_STORAGE_KEY(user.email), picked || "ALL");
    // Hard-reload the current page so every list / dashboard / cache refetches
    // against the new depot. Avoids per-page `[depot]` useEffect dependencies
    // scattered across the codebase and the subtle bugs they create (stale
    // Ghana totals leaking into a Togo view, mounted dialogs holding the
    // previous depot's data, etc). This is what you'd do switching accounts
    // in any multi-tenant admin console.
    setTimeout(() => window.location.reload(), 50);
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadDepots(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [loadDepots]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      await loadDepots(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const { data } = await api.post("/auth/google", { credential });
      setUser(data.user);
      await loadDepots(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(false);
    setApiDepot("");
    setDepotState("");
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, loginWithGoogle, logout,
      refresh: checkAuth,
      depots, depot, setDepot,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
